import React, { useEffect, useRef, useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumb from "./Breadcrumb";
import MobileBottomNav from "./MobileBottomNav";
import ChatWidget from "../messaging/ChatWidget";
import WelcomePopup from "../winter/WelcomePopup";
import AddressUpdateAnnouncement from "../AddressUpdateAnnouncement";
import OpenInAppBanner from "../common/OpenInAppBanner";
import { useLocation, useSearchParams } from "react-router-dom";
import { gsap } from "gsap";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // Define auth pages that should not show navigation elements
  const isAuthPage = [
    "/login",
    "/register",
    "/forgot-password",
    "/forgot-password-success",
    "/email-verification",
  ].includes(location.pathname);

  // Don't show breadcrumb on specific pages
  const showBreadcrumb =
    !isAuthPage &&
    location.pathname !== "/" &&
    !location.pathname.startsWith("/products") &&
    location.pathname !== "/categories" &&
    !location.pathname.startsWith("/store-page") &&
    location.pathname !== "/stores";

  const showFooter = !isAuthPage;
  const showMobileNav = !isAuthPage && location.pathname !== "/messaging";
  const showHeader = !isAuthPage;
  const showChatWidget = !isAuthPage; // Always show chat widget when not on auth pages

  // Animate main content on route change
  useEffect(() => {
    if (mainRef.current) {
      gsap.fromTo(
        mainRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.3, ease: "power2.out" }
      );
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {showHeader && <Header />}

      {/* Address Update Announcement Bar */}
      {!isAuthPage && (
        <AddressUpdateAnnouncement 
          position="top"
          delay={5000}
          scrollThreshold={30}
        />
      )}

      {/* Breadcrumb */}
      {showBreadcrumb && <Breadcrumb />}

      <main
        ref={mainRef}
        className="flex-1 pt-10 sm:pt-6 md:pt-10 pb-[calc(64px+env(safe-area-inset-bottom))]"
      >
        {children}
      </main>
      {showFooter && <Footer />}

      {/* Mobile Bottom Navigation */}
      {showMobileNav && <MobileBottomNav />}

      {/* Chat Widget - Modern messaging interface */}
      {showChatWidget && <ChatWidget />}

      {/* Welcome Popup - First visit */}
      {!isAuthPage && <WelcomePopup />}

      {/* Open in App Banner - Mobile only */}
      {!isAuthPage && <OpenInAppBanner />}
    </div>
  );
};

export default Layout;
